import React, { createContext, useContext, useReducer, useEffect, ReactNode, useRef } from 'react';
import { authService } from '../services/authService';

// Tipos
export interface User {
  id: string;
  username: string;
  email: string;
  role: 'ADMIN' | 'SUPERVISOR' | 'AGENT';
  permissions: string[];
  fullName?: string;
  avatar?: string;
  lastLogin?: string;
  isActive: boolean;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

// Acciones del reducer
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'UPDATE_USER'; payload: Partial<User> };

// Estado inicial
const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('dashboard_token'),
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };

    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    case 'UPDATE_USER':
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null,
      };

    default:
      return state;
  }
};

// Contexto
interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  updateUser: (userData: Partial<User>) => void;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string | string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const isLoggingOutRef = useRef(false);

  // Verificar token al cargar la aplicación.
  // Maneja 3 escenarios distintos:
  //   - 401 → token muerto, logout y mandar a /login
  //   - 429 → rate-limited, NO borrar el token, reintentar con backoff (auth/me ya no
  //           se rate-limitea en el backend pero por las dudas dejamos esta protección)
  //   - error de red u otro → no borrar el token, reintentar
  useEffect(() => {
    let cancelled = false;

    const tryGetUser = async (attempt = 0): Promise<void> => {
      const token = localStorage.getItem('dashboard_token');
      if (!token) return;
      if (cancelled) return;

      try {
        dispatch({ type: 'AUTH_START' });
        const userData = await authService.getCurrentUser();
        if (cancelled) return;
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user: userData, token },
        });
      } catch (error: any) {
        if (cancelled) return;
        const status = error.authStatus;
        console.error('Error al verificar token:', error?.message, '(status:', status, ')');

        if (status === 401) {
          // Sesión realmente inválida → logout
          localStorage.removeItem('dashboard_token');
          dispatch({
            type: 'AUTH_FAILURE',
            payload: 'Sesión expirada. Por favor, inicie sesión nuevamente.',
          });
          return;
        }

        if (status === 429 || status === 0 || !status) {
          // Rate-limit transitorio o error de red. Reintentar con backoff.
          if (attempt < 3) {
            const delay = (attempt + 1) * 2000; // 2s, 4s, 6s
            console.warn(`Reintentando auth/me en ${delay}ms (intento ${attempt + 1}/3)`);
            setTimeout(() => { if (!cancelled) tryGetUser(attempt + 1); }, delay);
            return;
          }
          // Después de 3 intentos sin lograr, NO borramos el token — quizá el server está
          // sobrecargado. Marcamos como "no autenticado" sin tirar /login para que la UI lo maneje.
          dispatch({
            type: 'AUTH_FAILURE',
            payload: 'No se pudo verificar la sesión. Reintentá en un momento.',
          });
          return;
        }

        // Cualquier otro código (5xx, etc.): no borrar token, mostrar error
        dispatch({
          type: 'AUTH_FAILURE',
          payload: 'Error al verificar sesión. Reintentá en un momento.',
        });
      }
    };

    tryGetUser();
    return () => { cancelled = true; };
  }, []);

  // Función de login
  const login = async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: 'AUTH_START' });

      const response = await authService.login(credentials);
      
      // Guardar token en localStorage
      localStorage.setItem('dashboard_token', response.token);
      
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: response.user,
          token: response.token,
        },
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Error al iniciar sesión. Verifique sus credenciales.';
      
      dispatch({
        type: 'AUTH_FAILURE',
        payload: errorMessage,
      });
      
      throw error;
    }
  };

  // Función de logout
  const logout = () => {
    // Prevenir múltiples llamadas simultáneas de logout
    if (isLoggingOutRef.current) {
      return;
    }
    
    isLoggingOutRef.current = true;
    
    try {
      // Llamar al servicio de logout solo si hay un token válido
      const token = localStorage.getItem('dashboard_token');
      if (token) {
        authService.logout().catch((error) => {
          // Si falla el logout del servidor, solo loguearlo
          console.warn('Error al cerrar sesión en el servidor:', error);
        });
      }
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      // Siempre limpiar localStorage y estado
      localStorage.removeItem('dashboard_token');
      dispatch({ type: 'LOGOUT' });
      isLoggingOutRef.current = false;
    }
  };

  // Limpiar error
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Actualizar datos del usuario
  const updateUser = (userData: Partial<User>) => {
    dispatch({ type: 'UPDATE_USER', payload: userData });
  };

  // Verificar permisos
  const hasPermission = (permission: string): boolean => {
    if (!state.user || !state.isAuthenticated) return false;
    
    // Los administradores tienen todos los permisos
    if (state.user.role === 'ADMIN') return true;
    
    return state.user.permissions.includes(permission);
  };

  // Verificar roles
  const hasRole = (role: string | string[]): boolean => {
    if (!state.user || !state.isAuthenticated) return false;
    
    if (Array.isArray(role)) {
      return role.includes(state.user.role);
    }
    
    return state.user.role === role;
  };

  // Interceptor para manejar errores de autenticación
  useEffect(() => {
    const handleAuthError = (error: any) => {
      if (error.response?.status === 401 && !isLoggingOutRef.current) {
        // Solo hacer logout si no estamos ya en proceso de logout
        logout();
      }
    };

    // Configurar interceptor de axios si es necesario
    authService.setAuthErrorHandler(handleAuthError);

    return () => {
      authService.removeAuthErrorHandler();
    };
  }, []);

  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    clearError,
    updateUser,
    hasPermission,
    hasRole,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook personalizado
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

// HOC para proteger rutas
interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermission?: string;
  requiredRole?: string | string[];
  fallback?: ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  requiredRole,
  fallback = <div>No tienes permisos para acceder a esta sección</div>,
}) => {
  const { isAuthenticated, hasPermission, hasRole } = useAuth();

  if (!isAuthenticated) {
    return <div>Debes iniciar sesión para acceder</div>;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <>{fallback}</>;
  }

  if (requiredRole && !hasRole(requiredRole)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

export default AuthContext;